import { IntelligentPhotoCaptureCameraConstants, IntelligentPhotoCaptureShutterBurstSequence, IntelligentPhotoCaptureShutterMinGain } from '../constants';
let sharedAudioCtx = null;
let sharedAudioCtxConstructor;
/**
 * Retrieves or initializes the shared Web Audio API AudioContext.
 * Resumes context if suspended due to user interaction policies.
 * @returns {AudioContext | null} The shared audio context instance (or null when unsupported).
 */
const getAudioContext = () => {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) {
        return null;
    }
    if (!sharedAudioCtx || sharedAudioCtxConstructor !== AudioCtx) {
        sharedAudioCtxConstructor = AudioCtx;
        sharedAudioCtx = new AudioCtx();
    }
    if (sharedAudioCtx.state === IntelligentPhotoCaptureCameraConstants.SUSPENDED_STATE) {
        void sharedAudioCtx.resume().catch(() => undefined);
    }
    return sharedAudioCtx;
};
/**
 * Generates and plays a single synthetic noise burst through the audio context.
 * Applies frequency filtering and gain envelope to the generated white noise.
 * @param {AudioContext} ctx - The Web Audio API audio context for playback.
 * @param {IIntelligentPhotoCaptureNoiseBurstConfig} config - Burst configuration including timing, filter, and envelope parameters.
 * @returns {void}
 */
const createNoiseBurst = (ctx, { duration, startTime, filterType, filterFreq, filterQ = 1, peakGain, attack = 0.001 }) => {
    const bufferSize = ctx.sampleRate * duration;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
    }
    const source = ctx.createBufferSource();
    source.buffer = buffer;
    const filter = ctx.createBiquadFilter();
    filter.type = filterType;
    filter.frequency.value = filterFreq;
    filter.Q.value = filterQ;
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0, startTime);
    gain.gain.linearRampToValueAtTime(peakGain, startTime + attack);
    gain.gain.exponentialRampToValueAtTime(IntelligentPhotoCaptureShutterMinGain, startTime + duration);
    source
        .connect(filter)
        .connect(gain)
        .connect(ctx.destination);
    source.start(startTime);
    source.stop(startTime + duration);
};
/**
 * Plays the complete intelligent photo capture shutter sound by synthesizing
 * multiple overlapping noise bursts with filtered gain envelopes.
 * Uses a pre-defined sequence of burst parameters to create a realistic shutter sound effect.
 * @returns {void}
 */
export const intelligentPhotoCaptureShutterSound = () => {
    const ctx = getAudioContext();
    if (!ctx) {
        return;
    }
    const now = ctx.currentTime;
    IntelligentPhotoCaptureShutterBurstSequence.forEach((burst) => {
        createNoiseBurst(ctx, Object.assign(Object.assign({}, burst), { startTime: now + burst.timeOffset }));
    });
};
//# sourceMappingURL=intelligentPhotoCaptureShutterSoundUtilities.js.map